import React, { useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import { UseControllerProps, useController } from 'react-hook-form';

import { Link as LinkIcon } from '@mui/icons-material';
import { Autocomplete, Box, Chip, InputAdornment, Link, TextField } from '@mui/material';

import PriceParrotService from '~/services/PriceParrotService';

import { Property } from '~/types/services/priceParrot';

type LocationAutocompleteProps<T> = {
  control: UseControllerProps<T>['control'];
  name: UseControllerProps<T>['name'];
  label?: string;
  disabled?: boolean;
  rules?: UseControllerProps<T>['rules'];
};

export default function LocationAutocomplete<T>({
  control,
  name,
  label = 'Location Link',
  disabled = false,
  rules,
}: LocationAutocompleteProps<T>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [propertySearchOptions, setPropertySearchOptions] = useState<Array<Property>>([]);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const handleSearchProperties = useMemo(
    () =>
      debounce((searchTerm: string): void => {
        setPropertySearchOptions([]);
        if (searchTerm.length < 2) {
          return;
        }

        setLoading(true);
        PriceParrotService.searchProperty(searchTerm)
          .then(({ data }) => {
            setPropertySearchOptions(data);
          })
          .catch(console.error)
          .finally(() => setLoading(false));
      }, 400),
    [],
  );

  return (
    <Autocomplete
      /* In MUI v5 there doesn't exist renderValue prop, so we need to use multiple + limitTags + renderTags instead*/
      multiple
      limitTags={1}
      renderTags={(value: Array<Property>, getTagProps) => {
        return value[0] ? <Chip label={value[0].name} size="small" {...getTagProps({ index: 0 })} /> : null;
      }}
      freeSolo
      loading={loading}
      options={propertySearchOptions}
      getOptionKey={(option: Property) => option.url}
      getOptionLabel={(option: Property) => option.name}
      /* Disable client side filtering */
      filterOptions={(options) => options}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Link
            variant="body1"
            href={option.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            underline="hover"
          >
            {option.name + ' | ' + option.location}
          </Link>
        </Box>
      )}
      onInputChange={(_, newInputValue: string, reason: string) => {
        if (reason === 'input') {
          handleSearchProperties(newInputValue);
        } else if (reason === 'clear') {
          setPropertySearchOptions([]);
        }

        field.onChange(newInputValue);
      }}
      /* Use the url field of the selected option to update the field value */
      onChange={(_, newValue: Array<Property> | null) => field.onChange(newValue?.[0]?.url ?? '')}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          disabled={disabled}
          error={!!error}
          helperText={error?.message}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <LinkIcon fontSize="small" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
